import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import HelpFab from "@/components/HelpFab";
import api from "@/lib/api";
import {
  Check, ChevronRight, ExternalLink, ShieldCheck, Smartphone, Monitor,
  Wallet, Network, Coins, Cpu, Zap, AlertCircle, Loader2, X, RefreshCw, QrCode, Copy
} from "lucide-react";
import SeedBackupCoach from "@/components/SeedBackupCoach";

// =========================================================
//  BOT CHAIN + CONTRACTS  (same source-of-truth as Onboarding)
// =========================================================
const BOT_CHAIN = {
  chainId: "0x2A5", // 677
  chainName: "BOT Chain",
  rpcUrls: ["https://rpc.botchain.ai"],
  nativeCurrency: { name: "BOT", symbol: "BOT", decimals: 18 },
  blockExplorerUrls: ["https://scan.botchain.ai"],
};
const CONTRACTS = {
  USDT: { address: "0xaBabc7Ddc03e501d190C676BF3d92ef0e6e87a3C", symbol: "USDT", decimals: 6,  image: "https://assets.coingecko.com/coins/images/325/standard/Tether.png" },
  CA:   { address: "0x546307af427902A75771434Df831d88219784E19", symbol: "CA",   decimals: 18, image: "" },
};

// =========================================================
//  DEVICE / WALLET DETECTION
// =========================================================
function detectDevice() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

function detectTPWallet() {
  if (typeof window === "undefined") return { detected: false, source: null };
  const eth = window.ethereum;
  if (eth?.isTokenPocket) return { detected: true, source: "ethereum.isTokenPocket" };
  if (window.tokenpocket) return { detected: true, source: "window.tokenpocket" };
  // EIP-6963: TP also broadcasts itself — but for a snapshot at mount, this is enough.
  return { detected: false, source: null };
}

function detectBrowser() {
  if (typeof navigator === "undefined") return "Browser";
  const ua = navigator.userAgent;
  if (/Edg\//i.test(ua)) return "Edge";
  if (/Brave/i.test(ua) || (navigator.brave && navigator.brave.isBrave)) return "Brave";
  if (/Chrome/i.test(ua)) return "Chrome";
  if (/Firefox/i.test(ua)) return "Firefox";
  if (/Safari/i.test(ua)) return "Safari";
  return "Browser";
}

const TP_EXTENSION_URL = "https://chromewebstore.google.com/detail/tokenpocket-web3-crypto-w/mfgccjchihfkkindfppnaooecgfneiii";
const TP_IOS_URL = "https://apps.apple.com/us/app/tokenpocket-crypto-bitcoin/id6444625622";
const TP_ANDROID_URL = "https://play.google.com/store/apps/details?id=vip.mytokenpocket";

function buildTPDeepLink(url) {
  // tpdapp://open?params={"url":"...","chain":"EVM"}
  const params = encodeURIComponent(JSON.stringify({ url, chain: "EVM", source: "BotChainCP" }));
  return `tpdapp://open?params=${params}`;
}

// =========================================================
//  STATUS PILL
// =========================================================
function StatusPill({ state, label }) {
  const styles = {
    pending: { bg: "rgba(15,18,38,0.05)", color: "#64748B", icon: <span className="w-2 h-2 rounded-full bg-current" /> },
    running: { bg: "rgba(64,96,255,0.12)", color: "#4060FF", icon: <Loader2 size={11} className="animate-spin" /> },
    done:    { bg: "rgba(0,200,83,0.13)",  color: "#00863A", icon: <Check size={11} /> },
    failed:  { bg: "rgba(255,107,107,0.15)", color: "#B91C1C", icon: <X size={11} /> },
  }[state] || {};
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ background: styles.bg, color: styles.color }}>
      {styles.icon} {label}
    </span>
  );
}

// =========================================================
//  ACTION CARD  — one auto-run RPC step on desktop
// =========================================================
function ActionCard({ num, icon, title, hint, status, error, onRun, onSkip, ctaLabel = "Run", disabled }) {
  return (
    <div className="glass p-5 fade-up flex items-start gap-4" data-testid={`action-card-${num}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: status === "done" ? "linear-gradient(135deg, #00C853, #00B344)" : "rgba(64,96,255,0.14)", color: status === "done" ? "white" : "#4060FF" }}>
        {status === "done" ? <Check size={18} /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-3">Action {num}</span>
          <StatusPill state={status || "pending"} label={status || "pending"} />
        </div>
        <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
        <p className="text-sm text-ink-2 mt-0.5">{hint}</p>
        {error && (
          <div className="mt-2 px-3 py-2.5 rounded-lg flex items-start gap-2 text-xs" style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.25)", color: "#991B1B" }} data-testid={`action-${num}-error`}>
            <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">{typeof error === "string" ? error : error.message}</div>
              {typeof error === "object" && error.fix && (
                <div className="mt-1.5 text-[12px] font-normal" style={{ color: "#7F1D1D" }}>
                  <b>How to fix:</b> {error.fix}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={onRun}
            disabled={disabled || status === "running" || status === "done"}
            className={status === "done" ? "btn-secondary" : "btn-primary"}
            style={{ padding: "8px 16px", fontSize: 13 }}
            data-testid={`action-${num}-run`}
          >
            {status === "done" ? <><Check size={13} /> Approved</> : status === "running" ? <><Loader2 size={13} className="animate-spin" /> Waiting for approval…</> : <>{ctaLabel} <ChevronRight size={13} /></>}
          </button>
          {status !== "done" && onSkip && (
            <button onClick={onSkip} className="btn-ghost" style={{ padding: "8px 12px", fontSize: 12 }} data-testid={`action-${num}-skip`}>
              Skip / already done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =========================================================
//  PERSISTENCE
// =========================================================
function useStep(id, initial = "pending") {
  const key = `cp_smart_${id}`;
  const [state, setState] = useState(() => { try { return localStorage.getItem(key) || initial; } catch { return initial; } });
  const set = (v) => { setState(v); try { localStorage.setItem(key, v); } catch {} };
  return [state, set];
}

// =========================================================
//  MAIN PAGE
// =========================================================
export default function SmartSetup() {
  const [params] = useSearchParams();
  const referralSlug = params.get("ref") || params.get("r");
  const [refInfo, setRefInfo] = useState(null);

  const [device, setDevice] = useState(() => detectDevice());
  const [browser] = useState(() => detectBrowser());
  const [{ detected: tpDetected, source: tpSource }, setTPDetect] = useState({ detected: false, source: null });
  const [manualMode, setManualMode] = useState(null); // null | "desktop" | "mobile"

  // Re-detect TP Wallet on mount, then poll once after 1s (extension may inject slightly late)
  useEffect(() => {
    setTPDetect(detectTPWallet());
    const t1 = setTimeout(() => setTPDetect(detectTPWallet()), 800);
    const t2 = setTimeout(() => setTPDetect(detectTPWallet()), 2200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    api.get(`/public/referral/${referralSlug || "stu"}`).then(({ data }) => setRefInfo(data)).catch(() => {});
  }, [referralSlug]);

  const effectiveDevice = manualMode || device;
  const isMobile = effectiveDevice === "ios" || effectiveDevice === "android";

  const carypactUrl = refInfo?.carypact_url || "https://app.carypact.com/?inviteCode=4zR0Wd&suffix=E6c327Stu";
  const inviteCode = refInfo?.cary_invite_code || "4zR0Wd";

  // ------ Action states (persisted) ------
  const [s1, setS1] = useStep("chain");  // BOT Chain added
  const [s2, setS2] = useStep("usdt");   // USDT imported
  const [s3, setS3] = useStep("ca");     // CA imported
  const [e1, setE1] = useState(null);
  const [e2, setE2] = useState(null);
  const [e3, setE3] = useState(null);

  const reset = () => {
    [setS1, setS2, setS3].forEach((s) => s("pending"));
    [setE1, setE2, setE3].forEach((s) => s(null));
  };

  // ------ Actions ------
  const provider = () => window.ethereum || (window.tokenpocket && window.tokenpocket.ethereum) || null;

  // Translate raw wallet error messages into user-friendly fixes.
  const friendlyError = (err) => {
    const raw = (err?.message || err?.toString() || "").toLowerCase();
    if (raw.includes("at least one account") || raw.includes("no accounts") || raw.includes("unauthorized")) {
      return {
        message: "Your TP Wallet extension is installed but no wallet has been created inside it yet.",
        fix: "Click the TP Wallet icon in your browser toolbar → Create a New Wallet (or Import) → back up your 12-word seed phrase → come back here and tap Run again.",
      };
    }
    if (raw.includes("user rejected") || raw.includes("user denied")) {
      return { message: "You rejected the approval in TP Wallet.", fix: "Tap Run again and approve the prompt in the extension popup." };
    }
    if (raw.includes("already pending")) {
      return { message: "TP Wallet has a previous popup still waiting.", fix: "Open the TP Wallet extension icon to clear the pending request, then tap Run again." };
    }
    return { message: err?.message || "Action failed.", fix: null };
  };

  const ensureUnlockedAccount = async (p) => {
    // Returns first account address, or throws a typed error if none.
    try {
      const accounts = await p.request({ method: "eth_requestAccounts" });
      if (!accounts || accounts.length === 0) {
        const e = new Error("Wallet has no accounts yet. Create or import a wallet inside the TP Wallet extension first.");
        e.code = "NO_ACCOUNT";
        throw e;
      }
      return accounts[0];
    } catch (err) {
      // Re-throw with a normalized message so friendlyError() can recognize it.
      if (err?.code === "NO_ACCOUNT") throw err;
      throw err;
    }
  };

  const runAddChain = async () => {
    setE1(null); setS1("running");
    const p = provider();
    if (!p) { setE1({ message: "TP Wallet is not detected.", fix: "Install the extension below, then refresh this page." }); setS1("failed"); return; }
    try {
      await ensureUnlockedAccount(p);
      await p.request({ method: "wallet_addEthereumChain", params: [BOT_CHAIN] });
      setS1("done");
    } catch (err) {
      setE1(friendlyError(err));
      setS1("failed");
    }
  };

  const runWatchAsset = async (which) => {
    const setS = which === "USDT" ? setS2 : setS3;
    const setE = which === "USDT" ? setE2 : setE3;
    setE(null); setS("running");
    const p = provider();
    if (!p) { setE({ message: "TP Wallet is not detected.", fix: "Install the extension below, then refresh this page." }); setS("failed"); return; }
    try {
      await ensureUnlockedAccount(p);
      // Try to switch to BOT Chain first so the asset is registered on the right network.
      try { await p.request({ method: "wallet_switchEthereumChain", params: [{ chainId: BOT_CHAIN.chainId }] }); } catch {}
      const c = CONTRACTS[which];
      const ok = await p.request({
        method: "wallet_watchAsset",
        params: { type: "ERC20", options: { address: c.address, symbol: c.symbol, decimals: c.decimals, image: c.image || undefined } },
      });
      if (ok === false) { setE({ message: "You declined the token import in TP Wallet.", fix: "Tap Import again and approve the prompt." }); setS("failed"); return; }
      setS("done");
    } catch (err) {
      setE(friendlyError(err));
      setS("failed");
    }
  };

  // ------ Progress ------
  const completed = useMemo(() => [s1, s2, s3].filter((s) => s === "done").length, [s1, s2, s3]);
  const allDone = completed === 3;

  return (
    <div className="min-h-screen pb-20">
      <Header variant="public" />

      {/* HERO */}
      <section className="px-6 md:px-10 pt-12">
        <div className="max-w-[1100px] mx-auto">
          <div className="pill mb-5" data-testid="setup-pill">
            <span className="dot" /> Smart Setup · TP Wallet only · {browser} on {effectiveDevice}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] tracking-tight mb-4">
            One-click <span className="gradient-text">wallet setup</span> — TP Wallet, BOT Chain, CaryPact.
          </h1>
          <p className="text-lg text-ink-2 max-w-2xl">
            Three approval clicks and you're ready to buy your first hashrate. We add the BOT Chain network and register both tokens for you — you only sign in your wallet.
          </p>
          {refInfo && (
            <div className="mt-5 pill" data-testid="setup-sponsor">
              <span className="dot" style={{ background: "#4060FF" }} /> Your invitation <span className="text-ink-3 font-mono">· code {inviteCode}</span>
            </div>
          )}

          {/* Device toggle */}
          <div className="mt-7 inline-flex items-center gap-1 p-1 rounded-xl glass-sm" style={{ background: "rgba(255,255,255,0.55)" }}>
            <button
              onClick={() => { setManualMode("desktop"); setDevice("desktop"); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${!isMobile ? "bg-white shadow-sm text-ink" : "text-ink-2 hover:text-ink"}`}
              data-testid="mode-desktop"
            >
              <Monitor size={14} /> Desktop (TP extension)
            </button>
            <button
              onClick={() => { setManualMode("ios"); setDevice("ios"); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all ${isMobile ? "bg-white shadow-sm text-ink" : "text-ink-2 hover:text-ink"}`}
              data-testid="mode-mobile"
            >
              <Smartphone size={14} /> Mobile (TP Wallet app)
            </button>
          </div>
        </div>
      </section>

      {/* MAIN BODY */}
      <section className="px-6 md:px-10 mt-10">
        <div className="max-w-[1100px] mx-auto">
          {!isMobile ? (
            <DesktopFlow
              tpDetected={tpDetected}
              tpSource={tpSource}
              browser={browser}
              s1={s1} s2={s2} s3={s3}
              e1={e1} e2={e2} e3={e3}
              runAddChain={runAddChain}
              runWatchAsset={runWatchAsset}
              setS1={setS1} setS2={setS2} setS3={setS3}
              carypactUrl={carypactUrl}
              inviteCode={inviteCode}
              completed={completed}
              allDone={allDone}
              reset={reset}
              onRedetect={() => setTPDetect(detectTPWallet())}
            />
          ) : (
            <MobileFlow
              device={effectiveDevice}
              carypactUrl={carypactUrl}
              inviteCode={inviteCode}
            />
          )}
        </div>
      </section>

      {/* Fallback to manual guide */}
      <section className="px-6 md:px-10 mt-12">
        <div className="max-w-[1100px] mx-auto glass-strong p-6 flex flex-wrap items-center justify-between gap-4" data-testid="fallback-manual">
          <div>
            <div className="text-xs text-ink-3 font-mono uppercase tracking-wider mb-1">Prefer the long version?</div>
            <h3 className="font-display text-xl font-semibold tracking-tight">Read the 12-step manual</h3>
            <p className="text-sm text-ink-2 mt-1">Every screen, every tap — straight from the official TP Wallet × CaryPact PDF.</p>
          </div>
          <Link to="/flow" className="btn-secondary" style={{ padding: "10px 18px", fontSize: 13 }} data-testid="open-manual">Open the full guide <ChevronRight size={13} /></Link>
        </div>
      </section>

      <HelpFab />
    </div>
  );
}

// =========================================================
//  DESKTOP FLOW
// =========================================================
function DesktopFlow({
  tpDetected, tpSource, browser,
  s1, s2, s3, e1, e2, e3,
  runAddChain, runWatchAsset,
  setS1, setS2, setS3,
  carypactUrl, inviteCode,
  completed, allDone, reset, onRedetect,
}) {
  return (
    <>
      {/* Detection card */}
      <div className="glass-strong p-6 mb-6 fade-up" data-testid="tp-detection">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${tpDetected ? "" : ""}`} style={{ background: tpDetected ? "linear-gradient(135deg, #00C853, #00B344)" : "rgba(245,158,11,0.16)", color: tpDetected ? "white" : "#B45309" }}>
            {tpDetected ? <ShieldCheck size={22} /> : <AlertCircle size={22} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-ink-3 font-mono uppercase tracking-wider mb-1">Wallet detection</div>
            <h2 className="font-display text-2xl font-semibold tracking-tight">
              {tpDetected ? "TP Wallet extension detected ✓" : "TP Wallet extension not detected"}
            </h2>
            <p className="text-sm text-ink-2 mt-1">
              {tpDetected ? (
                <>Provider exposed via <span className="font-mono text-xs">{tpSource}</span>. You're ready to run the 3 auto-setup actions below.</>
              ) : (
                <>Install the TP Wallet extension in <b>{browser}</b>, finish wallet creation inside it, then come back and tap "I've installed it — re-check."</>
              )}
            </p>
            {tpDetected && (
              <div className="mt-3 px-3 py-2.5 rounded-lg text-xs flex items-start gap-2" style={{ background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.25)", color: "#7C2D12" }} data-testid="first-time-hint">
                <AlertCircle size={13} className="flex-shrink-0 mt-0.5" />
                <div>
                  <b>First time using the extension?</b> Click the TP Wallet icon in your browser toolbar and <b>create or import a wallet first</b> (set password → back up the 12-word seed phrase). The actions below need at least one account inside the wallet to work.
                </div>
              </div>
            )}
            {tpDetected && (
              <div className="mt-4 max-w-2xl" data-testid="seed-coach-wrapper">
                <SeedBackupCoach />
              </div>
            )}
            {!tpDetected && (
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <a href={TP_EXTENSION_URL} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ padding: "10px 16px", fontSize: 13 }} data-testid="install-tp-ext">
                  Install TP Wallet for {browser} <ExternalLink size={13} />
                </a>
                <button onClick={onRedetect} className="btn-secondary" style={{ padding: "10px 16px", fontSize: 13 }} data-testid="redetect-tp">
                  <RefreshCw size={13} /> I've installed it — re-check
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-xs text-ink-3 font-mono uppercase tracking-wider">Auto-setup progress</div>
          <div className="font-display text-3xl font-semibold tracking-tight mt-1" data-testid="progress-label">
            {completed} of 3 approved
          </div>
        </div>
        <button onClick={reset} className="btn-ghost" style={{ padding: "8px 12px", fontSize: 12 }} data-testid="reset-progress"><RefreshCw size={12} /> Reset</button>
      </div>
      <div className="h-2 w-full rounded-full mb-7 overflow-hidden" style={{ background: "rgba(15,18,38,0.06)" }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${(completed / 3) * 100}%`, background: "linear-gradient(90deg, #4060FF, #6B8AFF)" }} />
      </div>

      {/* Action cards */}
      <div className="space-y-4">
        <ActionCard
          num="1"
          icon={<Network size={18} />}
          title="Add BOT Chain to TP Wallet"
          hint="Adds the BOT Chain custom network (chainId 677, RPC rpc.botchain.ai) so your wallet can see CA balances and sign on-chain actions."
          status={s1}
          error={e1}
          onRun={runAddChain}
          onSkip={() => setS1("done")}
          ctaLabel="Add network"
          disabled={!tpDetected}
        />
        <ActionCard
          num="2"
          icon={<Coins size={18} />}
          title="Import USDT (BOT Chain) as a custom token"
          hint="Whitelists the official USDT contract (6 decimals). Without this, USDT balances on BOT Chain stay invisible in your wallet UI."
          status={s2}
          error={e2}
          onRun={() => runWatchAsset("USDT")}
          onSkip={() => setS2("done")}
          ctaLabel="Import USDT"
          disabled={!tpDetected || s1 !== "done"}
        />
        <ActionCard
          num="3"
          icon={<Coins size={18} />}
          title="Import CA (BOT Chain) as a custom token"
          hint="Whitelists the CA token contract (18 decimals). Your mining rewards land here once claimed."
          status={s3}
          error={e3}
          onRun={() => runWatchAsset("CA")}
          onSkip={() => setS3("done")}
          ctaLabel="Import CA"
          disabled={!tpDetected || s2 !== "done"}
        />
      </div>

      {/* Finish CTA */}
      <div className={`glass-strong p-7 fade-up mt-8 ${!allDone ? "opacity-60 pointer-events-none" : ""}`} style={{ background: allDone ? "linear-gradient(135deg, rgba(64,96,255,0.08), rgba(255,140,180,0.08))" : undefined }} data-testid="finish-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #4060FF, #6B8AFF)" }}>
            <Cpu size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-ink-3 font-mono uppercase tracking-wider mb-1">Final step</div>
            <h3 className="font-display text-2xl font-semibold tracking-tight">Open CaryPact with your invite pre-loaded</h3>
            <p className="text-sm text-ink-2 mt-1">
              CaryPact opens in a new tab, your TP Wallet extension auto-connects, and the invite code <b className="font-mono">{inviteCode}</b> is already filled in. From there you'll buy hashrate, claim CA, stake, and cash out — every action just needs one signature in the extension.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <a href={carypactUrl} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ padding: "12px 22px", fontSize: 14 }} data-testid="open-carypact-desktop">
                <Zap size={15} /> Open CaryPact dApp
              </a>
              <Link to="/flow" className="btn-secondary" style={{ padding: "12px 18px", fontSize: 13 }} data-testid="see-buy-hashrate">
                See the buy-hashrate walkthrough
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// =========================================================
//  MOBILE FLOW
// =========================================================
function MobileFlow({ device, carypactUrl, inviteCode }) {
  const [copied, setCopied] = useState(false);
  const targetUrl = typeof window !== "undefined" ? window.location.origin + "/setup" : "/setup";
  const tpDeepLink = buildTPDeepLink(targetUrl);
  const carypactInTP = buildTPDeepLink(carypactUrl);
  const storeUrl = device === "ios" ? TP_IOS_URL : TP_ANDROID_URL;
  const storeName = device === "ios" ? "App Store" : "Google Play";

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  return (
    <>
      {/* Step 1: Install TP Wallet from the store */}
      <div className="glass-strong p-6 mb-5 fade-up" data-testid="mobile-step-1">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #4060FF, #6B8AFF)" }}>
            <Smartphone size={20} />
          </div>
          <div className="flex-1">
            <div className="text-xs text-ink-3 font-mono uppercase tracking-wider mb-1">Step 1 of 3</div>
            <h3 className="font-display text-xl font-semibold tracking-tight">Install TP Wallet from the {storeName}</h3>
            <p className="text-sm text-ink-2 mt-1">Verify the developer is <b>TP Global Ltd</b>. Many clones exist — pick the wrong one and you lose your funds.</p>
            <a href={storeUrl} target="_blank" rel="noopener noreferrer" className="btn-primary mt-3" style={{ padding: "10px 18px", fontSize: 13 }} data-testid="install-tp-mobile">
              Open {storeName} <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </div>

      {/* Step 2: Open this wizard inside TP Wallet's Discover */}
      <div className="glass-strong p-6 mb-5 fade-up" data-testid="mobile-step-2">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #4060FF, #6B8AFF)" }}>
            <Wallet size={20} />
          </div>
          <div className="flex-1">
            <div className="text-xs text-ink-3 font-mono uppercase tracking-wider mb-1">Step 2 of 3</div>
            <h3 className="font-display text-xl font-semibold tracking-tight">Open this wizard inside TP Wallet</h3>
            <p className="text-sm text-ink-2 mt-1">
              Once TP Wallet is installed and a wallet is created (BNB Chain template — we add BOT Chain in the next screen), tap the button to bounce back into TP's Discover browser. From there the next three actions become single-tap approvals.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <a href={tpDeepLink} className="btn-primary" style={{ padding: "10px 18px", fontSize: 13 }} data-testid="open-in-tp">
                <Zap size={13} /> Open in TP Wallet
              </a>
              <button onClick={() => copy(targetUrl)} className="btn-secondary" style={{ padding: "10px 16px", fontSize: 12 }} data-testid="copy-wizard-url">
                <Copy size={12} /> {copied ? "Copied" : "Copy wizard URL"}
              </button>
            </div>
            <div className="mt-3 px-3 py-2.5 rounded-lg text-xs flex items-start gap-2 font-mono" style={{ background: "rgba(15,18,38,0.04)" }}>
              <QrCode size={14} className="flex-shrink-0 mt-0.5" />
              <div className="break-all text-ink-2">{tpDeepLink}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Step 3: Go to CaryPact */}
      <div className="glass-strong p-6 mb-5 fade-up" data-testid="mobile-step-3">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg, #4060FF, #6B8AFF)" }}>
            <Cpu size={20} />
          </div>
          <div className="flex-1">
            <div className="text-xs text-ink-3 font-mono uppercase tracking-wider mb-1">Step 3 of 3</div>
            <h3 className="font-display text-xl font-semibold tracking-tight">Open CaryPact (invite pre-loaded)</h3>
            <p className="text-sm text-ink-2 mt-1">Bounces straight into TP Wallet's Discover browser at CaryPact with code <b className="font-mono">{inviteCode}</b> filled in. After signing the connect message, you're earning.</p>
            <a href={carypactInTP} className="btn-primary mt-3" style={{ padding: "10px 18px", fontSize: 13 }} data-testid="open-carypact-mobile">
              <Zap size={13} /> Open CaryPact in TP Wallet
            </a>
          </div>
        </div>
      </div>

      <div className="text-xs text-ink-3 mt-3 text-center" data-testid="mobile-note">
        On mobile, BOT Chain + USDT/CA token import happen inside TP Wallet's network editor. If the auto-prompt doesn't fire, fall back to the <Link to="/flow" className="text-ink-2 underline">full flow guide</Link>.
      </div>
    </>
  );
}
